import * as XLSX from "xlsx-js-style";

export function exportToExcel(dataToExport) {
    if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
        return;
    }

    const safeValue = (value) => {
        if (value === null || value === undefined) {
            return "";
        }

        return String(value).trim();
    };

    const formatPhone = (phone) => {
        let digits = safeValue(phone).replace(/\D/g, "");

        if (!digits) {
            return "";
        }

        /*
         * Remove o código do Brasil quando recebido:
         * 555193036928  -> 5193036928
         * 5551980512453 -> 51980512453
         */
        if (
            digits.startsWith("55") &&
            (digits.length === 12 || digits.length === 13)
        ) {
            digits = digits.slice(2);
        }

        /*
         * Celular com DDD:
         * 51980512453 -> (51) 98051-2453
         */
        if (digits.length === 11) {
            return `(${digits.slice(0, 2)}) ${digits.slice(
                2,
                7
            )}-${digits.slice(7)}`;
        }

        /*
         * Telefone fixo com DDD:
         * 5130306928 -> (51) 3030-6928
         */
        if (digits.length === 10) {
            return `(${digits.slice(0, 2)}) ${digits.slice(
                2,
                6
            )}-${digits.slice(6)}`;
        }

        /*
         * Número internacional já com "+".
         * Mantém visível caso não seja um telefone BR esperado.
         */
        return safeValue(phone);
    };

    const formatAddressForReport = (obj) => {
        const street = [
            safeValue(obj.end_logradouro_entrega),
            safeValue(obj.end_numero_entrega),
        ]
            .filter(Boolean)
            .join(", ");

        const location = [
            safeValue(obj.end_bairro_entrega),
            safeValue(obj.end_municipio_entrega),
            safeValue(obj.end_cep_entrega),
        ]
            .filter(Boolean)
            .join(" - ");

        return [street, location]
            .filter(Boolean)
            .join(" - ");
    };


    /*
     * ABA 1 — RELATÓRIO DE ENDEREÇOS
     */
    const rows = dataToExport.map((obj) => ({
        Nome: safeValue(obj.nome),
        Telefone: formatPhone(obj.telefone),
        Endereco: formatAddressForReport(obj),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);

    const headerCells = Object.keys(rows[0]);

    headerCells.forEach((key, index) => {
        const cellRef = XLSX.utils.encode_cell({
            r: 0,
            c: index,
        });

        if (!worksheet[cellRef]) {
            return;
        }

        worksheet[cellRef].s = {
            font: {
                bold: true,
            },
            fill: {
                fgColor: {
                    rgb: "D3D3D3",
                },
            },
            alignment: {
                vertical: "center",
            },
        };
    });

    worksheet["!cols"] = [
        { wch: 30 },
        { wch: 20 },
        { wch: 55 },
    ];

    rows.forEach((row, rowIndex) => {
        const fillColor =
            rowIndex % 2 === 0
                ? "FFFFFF"
                : "F0F0F0";

        Object.keys(row).forEach((key, colIndex) => {
            const cellRef = XLSX.utils.encode_cell({
                r: rowIndex + 1,
                c: colIndex,
            });

            if (!worksheet[cellRef]) {
                return;
            }

            worksheet[cellRef].s = {
                fill: {
                    fgColor: {
                        rgb: fillColor,
                    },
                },
                alignment: {
                    vertical: "center",
                    wrapText: true,
                },
            };
        });
    });

    /*
 * ABA 2 — ETIQUETAS
 *
 * Uma etiqueta embaixo da outra.
 */
    const labelsWorksheet = XLSX.utils.aoa_to_sheet([]);

    const merges = [];
    const rowHeights = [];

    const columnsPerLabel = 6;
    const labelHeight = 6;

    const setCell = ({
        row,
        column,
        value,
        style = {},
    }) => {
        const cellRef = XLSX.utils.encode_cell({
            r: row,
            c: column,
        });

        labelsWorksheet[cellRef] = {
            t: "s",
            v: value ?? "",
            s: style,
        };
    };

    const applyThickBoxBorder = ({
        startRow,
        endRow,
        startColumn,
        endColumn,
    }) => {
        for (let row = startRow; row <= endRow; row++) {
            for (
                let column = startColumn;
                column <= endColumn;
                column++
            ) {
                const cellRef = XLSX.utils.encode_cell({
                    r: row,
                    c: column,
                });

                if (!labelsWorksheet[cellRef]) {
                    labelsWorksheet[cellRef] = {
                        t: "s",
                        v: "",
                    };
                }

                const currentStyle =
                    labelsWorksheet[cellRef].s || {};

                const currentBorder =
                    currentStyle.border || {};

                labelsWorksheet[cellRef].s = {
                    ...currentStyle,

                    border: {
                        ...currentBorder,

                        ...(row === startRow
                            ? {
                                top: {
                                    style: "thick",
                                    color: {
                                        rgb: "000000",
                                    },
                                },
                            }
                            : {}),

                        ...(row === endRow
                            ? {
                                bottom: {
                                    style: "thick",
                                    color: {
                                        rgb: "000000",
                                    },
                                },
                            }
                            : {}),

                        ...(column === startColumn
                            ? {
                                left: {
                                    style: "thick",
                                    color: {
                                        rgb: "000000",
                                    },
                                },
                            }
                            : {}),

                        ...(column === endColumn
                            ? {
                                right: {
                                    style: "thick",
                                    color: {
                                        rgb: "000000",
                                    },
                                },
                            }
                            : {}),
                    },
                };
            }
        }
    };

    const createMerge = (
        startRow,
        startColumn,
        endRow,
        endColumn
    ) => {
        merges.push({
            s: {
                r: startRow,
                c: startColumn,
            },
            e: {
                r: endRow,
                c: endColumn,
            },
        });
    };

    const formatCep = (cep) => {
        const digits = safeValue(cep).replace(/\D/g, "");

        if (digits.length === 8) {
            return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        }

        return safeValue(cep);
    };

    const formatAddressForLabel = (seller) => {
        const logradouro = safeValue(
            seller.end_logradouro_entrega
        );

        const numero = safeValue(
            seller.end_numero_entrega
        );

        const complemento = safeValue(
            seller.end_complemento_entrega
        );

        const bairro = safeValue(
            seller.end_bairro_entrega
        );

        const municipio = safeValue(
            seller.end_municipio_entrega
        );

        const cep = formatCep(
            seller.end_cep_entrega
        );

        const street = [
            logradouro,
            numero,
        ]
            .filter(Boolean)
            .join(", ");

        const firstLine = [
            street,
            complemento,
        ]
            .filter(Boolean)
            .join(" - ");

        const secondLine = [
            bairro,
            municipio,
            cep,
        ]
            .filter(Boolean)
            .join(" - ");

        return {
            firstLine,
            secondLine,
        };
    };

    dataToExport.forEach((seller, index) => {
        const startRow = index * labelHeight;
        const startColumn = 0;
        const endColumn = columnsPerLabel - 1;

        const sellerName = safeValue(
            seller.nome
        ).toUpperCase();

        const sellerPhone = formatPhone(
            seller.telefone
        );

        const {
            firstLine: sellerAddressLine1,
            secondLine: sellerAddressLine2,
        } = formatAddressForLabel(seller);

        /*
         * Nome
         */
        setCell({
            row: startRow,
            column: startColumn,
            value: sellerName,
            style: {
                font: {
                    bold: true,
                    sz: 16,
                },
                alignment: {
                    horizontal: "center",
                    vertical: "center",
                    wrapText: true,
                },
                border: {
                    top: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                    left: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                    right: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                },
            },
        });

        createMerge(
            startRow,
            startColumn,
            startRow,
            endColumn
        );

        /*
         * Linha em branco
         */
        setCell({
            row: startRow + 1,
            column: startColumn,
            value: "",
            style: {
                border: {
                    left: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                    right: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                },
            },
        });

        createMerge(
            startRow + 1,
            startColumn,
            startRow + 1,
            endColumn
        );

        /*
         * Telefone
         */
        setCell({
            row: startRow + 2,
            column: startColumn,
            value: sellerPhone,
            style: {
                font: {
                    sz: 11,
                },
                alignment: {
                    horizontal: "center",
                    vertical: "center",
                },
                border: {
                    left: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                    right: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                },
            },
        });

        createMerge(
            startRow + 2,
            startColumn,
            startRow + 2,
            endColumn
        );

        /*
         * Endereço:
         *
         * Logradouro, número
         * Bairro - cidade - CEP
         */
        const addressLineStyle = {
            font: {
                sz: 11,
            },
            alignment: {
                horizontal: "center",
                vertical: "center",
                wrapText: true,
            },
            border: {
                left: {
                    style: "thin",
                    color: {
                        rgb: "808080",
                    },
                },
                right: {
                    style: "thin",
                    color: {
                        rgb: "808080",
                    },
                },
            },
        };

        /*
         * Primeira linha:
         * Logradouro, número - complemento
         */
        setCell({
            row: startRow + 3,
            column: startColumn,
            value: sellerAddressLine1,
            style: addressLineStyle,
        });

        createMerge(
            startRow + 3,
            startColumn,
            startRow + 3,
            endColumn
        );

        /*
         * Segunda linha:
         * Bairro - cidade - CEP
         */
        setCell({
            row: startRow + 4,
            column: startColumn,
            value: sellerAddressLine2,
            style: {
                ...addressLineStyle,
                border: {
                    ...addressLineStyle.border,
                    bottom: {
                        style: "thin",
                        color: {
                            rgb: "808080",
                        },
                    },
                },
            },
        });

        createMerge(
            startRow + 4,
            startColumn,
            startRow + 4,
            endColumn
        );
        /*
         * Altura das linhas.
         */
        rowHeights[startRow] = {
            hpt: 28,
        };

        rowHeights[startRow + 1] = {
            hpt: 7,
        };

        rowHeights[startRow + 2] = {
            hpt: 20,
        };

        rowHeights[startRow + 3] = {
            hpt: 22,
        };

        rowHeights[startRow + 4] = {
            hpt: 22,
        };

        rowHeights[startRow + 5] = {
            hpt: 6,
        };

        applyThickBoxBorder({
            startRow,
            endRow: startRow + 4,
            startColumn,
            endColumn,
        });

    });

    /*
     * Largura total da etiqueta.
     */
    labelsWorksheet["!cols"] = Array.from(
        {
            length: columnsPerLabel,
        },
        () => ({
            wch: 14,
        })
    );

    labelsWorksheet["!merges"] = merges;
    labelsWorksheet["!rows"] = rowHeights;


    const totalLabelRows =
        dataToExport.length * labelHeight;

    labelsWorksheet["!ref"] = XLSX.utils.encode_range({
        s: {
            r: 0,
            c: 0,
        },
        e: {
            r: Math.max(totalLabelRows - 1, 0),
            c: columnsPerLabel - 1,
        },
    });

    labelsWorksheet["!pageSetup"] = {
        orientation: "portrait",
        fitToWidth: 1,
        fitToHeight: 0,
        paperSize: 9,
    };

    labelsWorksheet["!margins"] = {
        left: 0.2,
        right: 0.2,
        top: 0.2,
        bottom: 0.2,
        header: 0,
        footer: 0,
    };

    /*
     * Criação do arquivo.
     */
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Enderecos"
    );

    XLSX.utils.book_append_sheet(
        workbook,
        labelsWorksheet,
        "Etiquetas"
    );

    XLSX.writeFile(
        workbook,
        "Endereços.xlsx",
        {
            bookType: "xlsx",
            showGridLines: false,
        }
    );
}